/**
 * Frontend Application Constants
 * Centralized location for all constants used throughout the application
 */

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  TIMEOUT: 30000, // 30 seconds
  USE_MOCK: process.env.REACT_APP_USE_MOCK === 'true',
};

// Timeouts and Delays (in milliseconds)
export const TIMEOUTS = {
  STATUS_UPDATE_SHORT: 2000,    // 2 seconds - Short status update
  STATUS_UPDATE_LONG: 5000,     // 5 seconds - Long status update
  POLLING_INTERVAL: 5000,       // 5 seconds - SMS polling interval
  DEBOUNCE_INPUT: 300,          // 300ms - Input debounce
  TOAST_DISPLAY: 3000,          // 3 seconds - Toast notification
};

// Order ID Validation
export const VALIDATION = {
  ORDER_ID_MIN_LENGTH: 10,
  ORDER_ID_REGEX: /^[0-9A-Z]+$/,
  ORDER_ID_ERROR_EN: 'Please enter a valid Order ID',
  ORDER_ID_ERROR_BM: 'Sila masukkan ID Pesanan yang sah',
};

// Order Status
export const ORDER_STATUS = {
  // Processing states
  IDLE: 'idle',
  VERIFYING: 'verifying',
  GETTING_PHONE: 'getting_phone',
  WAITING_OTP: 'waiting_otp',
  SHIPPING: 'shipping',
  COMPLETED: 'completed',
  ERROR: 'error',

  // Labels
  LABELS: {
    idle: { en: 'Ready', bm: 'Bersedia' },
    verifying: { en: 'Verifying order...', bm: 'Mengesahkan pesanan...' },
    getting_phone: { en: 'Getting phone number...', bm: 'Mendapatkan nombor telefon...' },
    waiting_otp: { en: 'Waiting for SMS...', bm: 'Menunggu SMS...' },
    shipping: { en: 'Shipping order...', bm: 'Menghantar pesanan...' },
    completed: { en: 'Completed!', bm: 'Selesai!' },
    error: { en: 'Error occurred', bm: 'Ralat berlaku' },
  }
};



// Shopee Order Status
export const SHOPEE_STATUS = {
  UNPAID: 'UNPAID',
  READY_TO_SHIP: 'READY_TO_SHIP',
  PROCESSED: 'PROCESSED',
  SHIPPED: 'SHIPPED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  INVOICE_PENDING: 'INVOICE_PENDING',
  TO_RETURN: 'TO_RETURN',
};

// Language Codes
export const LANGUAGES = {
  ENGLISH: 'en',
  MALAY: 'bm',
  DEFAULT: 'en',
};

// Local Storage Keys
export const STORAGE_KEYS = {
  LANGUAGE: 'language',
  LAST_ORDER_ID: 'lastOrderId',
  USER_PREFERENCES: 'userPreferences',
};

// URL Parameters
export const URL_PARAMS = {
  ORDER_ID: 'id',
  LANG: 'l',
};

// Copy Types
export const COPY_TYPES = {
  PHONE: 'phone',
  OTP: 'otp',
};

// Timeouts (in seconds)
export const TIMEOUT_SECONDS = {
  COPY_FEEDBACK: 2,
  CANCEL_WAIT: 120, // 2 minutes
  OTP_TIMEOUT: 1200, // 20 minutes
  STEP_DELAY_SHORT: 1,
  STEP_DELAY_LONG: 1.5,
  POLLING_INTERVAL: 5,
};

// Progress Bar
export const PROGRESS_BAR = {
  MAX_CANCEL_SECONDS: 120, // 2 minutes
};

// Default Messages
export const DEFAULT_MESSAGES = {
  FAILED_TO_LOAD: 'Failed to load order details',
  AN_ERROR_OCCURRED: 'An error occurred',
  FAILED_TO_GENERATE: 'Failed to generate SMS',
  FAILED_TO_RETRY: 'Failed to retry',
  RETRY_ERROR: 'An error occurred during retry',
  COPY_ERROR: 'Failed to copy',
  CANCEL_STATUS_ERROR: 'Failed to check cancel status',
};

// Routes
export const ROUTES = {
  HOME: '/',
  ORDER_DETAILS: '/order',
};

// API Response Status
export const API_RESPONSE = {
  SUCCESS: 'success',
  ERROR: 'error',
  LOADING: 'loading',
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: {
    en: 'Network error. Please check your connection.',
    bm: 'Ralat rangkaian. Sila semak sambungan anda.',
  },
  ORDER_NOT_FOUND: {
    en: 'Order not found',
    bm: 'Pesanan tidak dijumpai',
  },
  GENERIC_ERROR: {
    en: 'An error occurred. Please try again.',
    bm: 'Ralat berlaku. Sila cuba lagi.',
  },
  TIMEOUT_ERROR: {
    en: 'Request timed out. Please try again.',
    bm: 'Permintaan tamat masa. Sila cuba lagi.',
  },
};

// UI Constants
export const UI = {
  MAX_MOBILE_WIDTH: 768,
  ANIMATION_DURATION: 300,
  SPINNER_SIZE: {
    SMALL: 16,
    MEDIUM: 24,
    LARGE: 48,
  },
};

// Color Scheme (for consistency with Tailwind)
export const COLORS = {
  PRIMARY: 'cyan-500',
  PRIMARY_HOVER: 'cyan-600',
  SECONDARY: 'gray-500',
  SUCCESS: 'green-500',
  ERROR: 'red-500',
  WARNING: 'yellow-500',
  INFO: 'blue-500',
};

// External Links
export const LINKS = {
  SHOPEE_STORE: 'https://shopee.com.my/senvra',
  SUPPORT_EMAIL: 'senvra.enterprise@gmail.com',
  HELP_CENTER: '',
  TUTORIAL_VIDEO: `${process.env.PUBLIC_URL}/videos/tutorial.mp4`,
};

const constants = {
  API_CONFIG,
  TIMEOUTS,
  VALIDATION,
  ORDER_STATUS,
  SHOPEE_STATUS,
  LANGUAGES,
  STORAGE_KEYS,
  URL_PARAMS,
  COPY_TYPES,
  TIMEOUT_SECONDS,
  PROGRESS_BAR,
  DEFAULT_MESSAGES,
  ROUTES,
  API_RESPONSE,
  ERROR_MESSAGES,
  UI,
  COLORS,
  LINKS,
};

export { REGISTRATION_INSTRUCTIONS } from './registrationInstructions';

export default constants;
